// TODO: switch to @wandb/weave/components/RadixTooltip once it exposes Arrow.
import * as Tooltip from '@radix-ui/react-tooltip';
import {Icon, IconButton} from '@wandb/components';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useState} from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import type {UnseenSettledThread} from '../../agentChat/threadReadStatus';
import {AGENT_NAME} from './constants';

export const WBAgentChatButton = ({
  isLoading,
  showIntroTooltip,
  hideIntroTooltip,
  unseenSettledThread,
  onImpression,
  onPress,
  ...props
}: AriaButtonProps & {
  isLoading?: boolean;
  showIntroTooltip?: boolean;
  hideIntroTooltip: () => void;
  unseenSettledThread: UnseenSettledThread | null;
  onImpression?: () => void;
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const dismissTooltip = () => {
    hideIntroTooltip();
    setIsTooltipOpen(false);
  };
  const handlePress: AriaButtonProps['onPress'] = event => {
    dismissTooltip();
    onPress?.(event);
  };

  // Fire once on mount — the button only renders when visible (i.e. the agent
  // is enabled for the user).
  useEffect(() => {
    onImpression?.();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const button = (
    <AriaButton
      aria-label={isLoading ? `${AGENT_NAME} (loading)` : AGENT_NAME}
      {...props}
      onPress={handlePress}
      className="group relative rounded-full p-4 mr-8">
      {showIntroTooltip && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-4 rounded-[5px] bg-[#CDF4F7]/50 blur-[8px] dark:blur-[8px] motion-safe:animate-pulse motion-reduce:opacity-60"
        />
      )}
      <div className="relative inline-flex h-32 pl-12 pr-14 items-center gap-8 rounded-[5px] border border-moon-550  bg-white/[0.06] text-white font-['Source_Sans_3',sans-serif] text-base font-semibold whitespace-nowrap outline outline-2 outline-transparent group-hover:border-moon-450 group-hover:bg-white/[0.11] group-focus-visible:outline-teal-500 group-focus-visible:rounded-[9px] group-focus-visible:outline-offset-2 group-aria-[expanded=true]:border-teal-500 group-aria-[expanded=true]:bg-teal-500/[0.14]">
        <span
          className={
            isLoading
              ? 'inline-flex animate-spin [animation-duration:4s]'
              : 'inline-flex'
          }>
          <Icon
            icon={isLoading ? 'loading' : 'lightboard-solid'}
            size="md"
            color={isLoading ? 'moon-50' : 'teal-500'}
          />
        </span>
        Ask {AGENT_NAME}
      </div>
      {unseenSettledThread != null && (
        <div
          role="img"
          aria-label="Unread notice"
          className="absolute -right-2 -top-5">
          <Icon icon="circle-filled" size="xs" color="red-500" />
        </div>
      )}
    </AriaButton>
  );

  return (
    <Tailwind>
      <Tooltip.Provider delayDuration={0}>
        <Tooltip.Root
          open={showIntroTooltip || isTooltipOpen}
          onOpenChange={setIsTooltipOpen}>
          <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
          <Tooltip.Portal>
            <TooltipContent
              side="bottom"
              sideOffset={8}
              className="night-aware relative z-[999999] max-w-[300px] rounded text-sm leading-[140%] shadow-md bg-moon-900 p-12 text-moon-200 dark:bg-moon-50 dark:text-moon-800">
              <div>
                <div className="font-semibold text-moon-100 dark:text-moon-900">
                  {AGENT_NAME}: a coding agent for ML experiments
                </div>
                {showIntroTooltip && (
                  <div className="absolute right-8 top-8">
                    <IconButton
                      aria-label={`Dismiss ${AGENT_NAME} tooltip`}
                      icon="close"
                      size="sm"
                      variant="ghost"
                      onPress={dismissTooltip}
                    />
                  </div>
                )}
              </div>
              <div className="mt-4 text-[#C7CCD1] dark:text-moon-650">
                Analyze and visualize runs, write code, launch experiments, or
                ask any W&B question
              </div>
              <Tooltip.Arrow className="fill-moon-900 dark:fill-moon-50" />
            </TooltipContent>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </Tailwind>
  );
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof Tooltip.Content>,
  React.ComponentPropsWithoutRef<typeof Tooltip.Content>
>((props, ref) => (
  <TailwindContents>
    <Tooltip.Content ref={ref} {...props} />
  </TailwindContents>
));
